import { useCallback, useContext, useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { bookingState } from '../client-state/booking-state-context';
import useLocale from '../../../Hooks/use-locale';
import Calendar from '../../../ui-components/calendar/calendar';
import { selecetedDateActionTypes } from '../../../redusers/selectedDateReducer';
import useBookingShop from '../server-data/use-booking-shop';
import useOpenDays from '../../../Hooks/use-open-days';

const CalendarWithData = () => {
  const { data: shop } = useBookingShop();
  const { state, dispatch } = useContext(bookingState);
  const { languageAsEnum } = useLocale();
  // Initial value is null to prevent fetching open days using date in users timezone
  const [activeDate, setActiveDate] = useState<Date | null>(null);

  useEffect(() => {
    if (shop?.timezoneName) {
      if (!state.selectedDate) {
        // Set activeDate to start of current month
        setActiveDate(
          DateTime.now()
            .setZone(shop?.timezoneName)
            .setZone('local', { keepLocalTime: true })
            .startOf('month')
            .toJSDate()
        );
      } else {
        // Sync activeDate to selectedDate if out of sync
        const selectedMonth = DateTime.fromJSDate(state.selectedDate).month;
        const activeMonth = activeDate ? DateTime.fromJSDate(activeDate).month : null;

        if (selectedMonth !== activeMonth) {
          setActiveDate(
            DateTime.fromJSDate(state.selectedDate).startOf('month').toJSDate()
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedDate, shop?.timezoneName]);

  const {
    data: openDays,
    futureOpenDays,
    error: openDaysError,
    isFetching,
  } = useOpenDays(activeDate);

  const getDay = useCallback(
    (date: Date) => openDays?.find((day) => day.date === date.getDate()),
    [openDays]
  );
  const isFutureDay = useCallback(
    (date: Date) => !!futureOpenDays.find((day) => day.date === date.getDate()),
    [futureOpenDays]
  );

  return (
    <Calendar
      selectedDate={state.selectedDate}
      activeDate={activeDate}
      setActiveDate={setActiveDate}
      futureOpenDays={futureOpenDays}
      isFutureDay={isFutureDay}
      getDay={getDay}
      onChange={(date: Date) => {
        dispatch({ type: selecetedDateActionTypes.SELECTED_DATE_UPDATE, payload: date });

        const timeslots = document.getElementById('timeslots');
        if (timeslots) {
          setTimeout(() => {
            timeslots.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 500);
        }
      }}
      timezoneName={shop?.timezoneName}
      helpText={shop?.bookingLocalizations?.calendarHelpText?.[languageAsEnum]}
      openDaysError={openDaysError instanceof Error ? openDaysError.message : undefined}
      error={state.errors.priceModel ?? undefined}
      loading={isFetching}
    />
  );
};

export default CalendarWithData;
